import type { ButtonProps } from '@heroui/react';

/**
 * One of the three action buttons in the Purchases page header.
 * Keyed by slug so the View can stably address each item via
 * `getNodeTestIdProp('purchase-header-action-${slug}')`.
 */
export type PurchaseHeaderActionKey =
    'manualImport' | 'excelImport' | 'excelTemplate';

export type PurchaseHeaderAction = {
    /** i18n key (relative to the `pages` namespace) for the button label. */
    labelKey: string;
    /** Iconify identifier rendered as the leading icon. */
    icon: string;
    /** Stable slug used in DOM ids / data-testids. */
    slug: PurchaseHeaderActionKey;
    /**
     * HeroUI Button variant. Choices map to action hierarchy:
     *   - `primary`   → main CTA (Manual import).
     *   - `secondary` → supporting filled action (Excel import).
     *   - `outline`   → tertiary low-emphasis action (Excel template).
     * See HeroUI's design taste: solid for primary, outline/ghost for
     * non-primary, so a quick scan tells the user which action is "the"
     * action vs. side options.
     */
    variant: NonNullable<ButtonProps['variant']>;
};

/**
 * Ordered list of header actions. Order matters visually — the primary CTA
 * is rendered last in the row so it sits on the trailing edge (closest to
 * where the user's eye lands when scanning a right-aligned button group).
 */
export const PURCHASE_HEADER_ACTIONS: readonly PurchaseHeaderAction[] = [
    {
        labelKey: 'purchases.actions.excelTemplate',
        icon: 'mdi:file-download-outline',
        slug: 'excelTemplate',
        variant: 'secondary',
    },
    {
        labelKey: 'purchases.actions.excelImport',
        icon: 'mdi:file-excel-outline',
        slug: 'excelImport',
        variant: 'secondary',
    },
    {
        labelKey: 'purchases.actions.manualImport',
        icon: 'mdi:invoice-import-outline',
        slug: 'manualImport',
        variant: 'primary',
    },
];
